import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { apiFetch } from "@/lib/api";

export interface FeedbackProductInfo {
  key: string; // memo | pitch | bp | benchmark | trends
  name: string;
  defaultPrice: number; // EUR
}

interface Props {
  product: FeedbackProductInfo;
  onClose: () => void;
}

type Priority = "p1" | "interested" | "not_for_me";

export function FeedbackProductModal({ product, onClose }: Props) {
  const [priority, setPriority] = useState<Priority | null>(null);
  const [price, setPrice] = useState<number>(product.defaultPrice);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const submit = async () => {
    if (!priority) {
      toast.error("Sélectionnez une priorité.");
      return;
    }
    setSubmitting(true);
    const payload = {
      product_key: product.key,
      priority,
      target_price_eur: price,
      comment: comment.trim() || null,
      submitted_at: new Date().toISOString(),
    };
    try {
      await apiFetch("/api/feedback/product-interest", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      toast.success("Vote enregistré. Merci pour votre contribution.");
    } catch {
      // Fail-soft: store locally
      try {
        const key = `qorum_feedback_pending_${product.key}`;
        const existing = JSON.parse(localStorage.getItem(key) ?? "[]");
        existing.push(payload);
        localStorage.setItem(key, JSON.stringify(existing));
      } catch {
        /* ignore */
      }
      toast.success("Merci ! Votre vote sera transmis dès que possible.");
    } finally {
      setSubmitting(false);
      onClose();
    }
  };

  const radio = (val: Priority, label: string) => {
    const active = priority === val;
    return (
      <button
        key={val}
        type="button"
        onClick={() => setPriority(val)}
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: 11,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          padding: "10px 14px",
          border: `1px solid ${active ? "var(--leather)" : "var(--stone-mid)"}`,
          background: active ? "var(--leather)" : "transparent",
          color: active ? "var(--ivory)" : "var(--charcoal)",
          cursor: "pointer",
          transition: "all .15s",
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
      data-testid="feedback-product-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 250,
        background: "rgba(20,20,22,0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--ivory)",
          maxWidth: 520,
          width: "100%",
          padding: "32px 32px 24px",
          border: "0.5px solid var(--stone-mid)",
          boxShadow: "10px 10px 0 var(--accent-cramoisi)",
          position: "relative",
        }}
      >
        <button
          type="button"
          aria-label="Fermer"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--graphite)",
          }}
        >
          <X size={18} />
        </button>

        <h2
          id="feedback-modal-title"
          style={{
            fontFamily: '"Fraunces", serif',
            fontWeight: 700,
            fontSize: 24,
            color: "var(--leather)",
            margin: "0 0 8px",
            lineHeight: 1.2,
          }}
          data-testid="feedback-modal-title"
        >
          Aidez-nous à prioriser le {product.name}
        </h2>
        <p
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: "italic",
            fontSize: 14,
            color: "var(--smoke)",
            margin: "0 0 24px",
          }}
        >
          Votre avis sera traité avant le lancement de ce livrable. Merci d'avance.
        </p>

        <fieldset style={{ border: "none", padding: 0, margin: "0 0 20px" }}>
          <legend
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "var(--graphite)",
              marginBottom: 10,
            }}
          >
            Voteriez-vous pour ce livrable en priorité ?
          </legend>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {radio("p1", "Oui, priorité 1")}
            {radio("interested", "Intéressé")}
            {radio("not_for_me", "Pas pour moi")}
          </div>
        </fieldset>

        <div style={{ margin: "0 0 20px" }}>
          <label
            htmlFor="feedback-price"
            style={{
              display: "block",
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "var(--graphite)",
              marginBottom: 10,
            }}
          >
            Combien seriez-vous prêt à payer ?
          </label>
          <input
            id="feedback-price"
            type="range"
            min={0}
            max={500}
            step={5}
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            style={{ width: "100%", accentColor: "var(--leather)" }}
          />
          <div
            style={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 600,
              fontSize: 20,
              color: "var(--leather)",
              marginTop: 4,
            }}
          >
            {price} €
          </div>
        </div>

        <div style={{ margin: "0 0 24px" }}>
          <label
            htmlFor="feedback-comment"
            style={{
              display: "block",
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "var(--graphite)",
              marginBottom: 8,
            }}
          >
            Commentaire libre (optionnel)
          </label>
          <textarea
            id="feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              padding: 10,
              border: "0.5px solid var(--stone-mid)",
              background: "var(--paper)",
              color: "var(--charcoal)",
              resize: "vertical",
            }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              padding: "11px 18px",
              border: "1px solid var(--stone-mid)",
              background: "transparent",
              color: "var(--graphite)",
              cursor: "pointer",
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="feedback-submit"
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: 2,
              textTransform: "uppercase",
              padding: "11px 22px",
              border: "1px solid var(--leather)",
              background: "var(--leather)",
              color: "var(--ivory)",
              cursor: submitting ? "wait" : "pointer",
              fontWeight: 700,
            }}
          >
            {submitting ? "Envoi…" : "Envoyer →"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FeedbackProductModal;
